import { Button, Flex, Typography } from 'antd'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { ChecklistClickTarget } from 'src/analytics'
import { ChecklistClickTarget as ClickTarget } from 'src/analytics'

import { useStyles } from './styles'
import type { ChecklistItemModel } from './types'

const { Text } = Typography

type Props = {
  item: ChecklistItemModel
  onOpen: (item: ChecklistItemModel, target: ChecklistClickTarget) => void
}

/** The right-hand pane: what the task is, and the two ways to act on it. */
export const ChecklistItemCard = ({ item, onOpen }: Props) => {
  const { styles } = useStyles()
  const { t } = useTranslation()

  const { def } = item
  const base = `shell.gettingStarted.items.${def.key}` as const
  // The task's own icon, the same one the row on the left wears.
  const Icon = def.icon

  return (
    <div className={styles.card}>
      {/* Decorative: the title right below says the same thing. */}
      <img className={styles.cardImage} src={def.image} alt="" />
      <span className={styles.cardTitle}>{t(`${base}.title`)}</span>
      <span className={styles.cardText}>{t(`${base}.description`)}</span>
      {/* A step the platform marked failed. The code is the platform's own —
          an unknown one falls back to a generic line rather than printing a
          machine name at somebody. */}
      {item.errorCode ? (
        <Text type="danger">
          {t(`shell.gettingStarted.errors.${item.errorCode}`, {
            defaultValue: t('shell.gettingStarted.errors.generic')
          })}
        </Text>
      ) : null}
      {/*
        One row, always.

        The two buttons are what nearly broke it: `Открыть безопасность` plus a
        bordered `Подробнее` came to ~307px in a 303px card and wrapped. So the CTA
        labels are verbs only — the card title right above already names the task —
        and the docs link is borderless, which costs it its padding and border. The
        row still allows wrapping as a last resort for a translation nobody has
        written yet; the image gives up the height if it ever happens.
      */}
      <Flex align="center" gap={8} wrap="wrap">
        <Button
          type="primary"
          size="small"
          icon={<Icon size={14} />}
          onClick={() => onOpen(item, ClickTarget.PRIMARY)}
        >
          {t(`${base}.cta`)}
        </Button>
        <Button
          type="link"
          size="small"
          href={def.docsUrl}
          target="_blank"
          rel="noreferrer"
          icon={<ExternalLink size={13} />}
          iconPlacement="end"
          className={styles.cardDocsLink}
          onClick={() => onOpen(item, ClickTarget.DOCS)}
        >
          {t('shell.gettingStarted.learnMore')}
        </Button>
      </Flex>
    </div>
  )
}
